import { networkService } from '@/services/network.service';
import { syncQueueService } from './sync-queue-manager';
import { syncValidationService } from './SyncValidationService';
import OfflineLogger from '@/utils/offlineLogger';

export interface CacheEntry {
  key: string;
  data: any;
  timestamp: number;
  ttl: number; // Time to live in ms
  size: number; // Estimated size in bytes
}

export interface OptimizationConfig {
  enableLazyLoading: boolean;
  enablePrefetch: boolean;
  maxCacheSize: number; // MB
  defaultTtl: number; // ms
  prefetchDelay: number; // ms
  compressionEnabled: boolean;
}

class SyncOptimizationService {
  private cache = new Map<string, CacheEntry>();
  private maxCacheSize = 50 * 1024 * 1024; // 50MB default
  private currentCacheSize = 0;
  private prefetchTimer: NodeJS.Timeout | null = null;
  private cacheHits = 0;
  private cacheMisses = 0;
  
  private config: OptimizationConfig = {
    enableLazyLoading: true,
    enablePrefetch: true,
    maxCacheSize: 50 * 1024 * 1024, // 50MB
    defaultTtl: 30 * 60 * 1000, // 30 minuti
    prefetchDelay: 5000, // 5 secondi
    compressionEnabled: true
  };

  async performOptimizedInitialSync(
    userId: string,
    expectEmpty = false
  ): Promise<{
    success: boolean;
    strategy: string;
    entitiesSynced: string[];
    duration: number;
  }> {
    const startTime = Date.now();
    const entitiesSynced: string[] = [];
    let strategy = 'standard';

    OfflineLogger.info('SyncOptimizationService', 'Starting optimized initial sync');

    try {
      // 1. Priorità critica: Sync immediato delle entità essenziali
      const criticalEntities = ['categories', 'products', 'shoppingLists'];
      
      for (const entity of criticalEntities) {
        await syncQueueService.addTask(entity, userId, 'full-sync', 1, 15000);
        entitiesSynced.push(entity);
      }

      // Attendi che le entità critiche siano processate
      await syncQueueService.waitForCompletion(30000);

      // 2. Validazione delle entità critiche
      OfflineLogger.info('SyncOptimizationService', 'Invoking performComprehensiveValidation', {
        userId,
        stack: new Error().stack
      });
      const validation = await syncValidationService.performComprehensiveValidation(
        userId,
        expectEmpty
      );
      
      if (!validation.isValid && validation.criticalMissing.length > 0) {
        OfflineLogger.warn('SyncOptimizationService', 'Critical validation failed, falling back to full sync');
        strategy = 'fallback-full';
        
        // Fallback: sync completo se validazione fallisce
        const allEntities = ['shops', 'recurringProducts', 'shoppingItems'];
        for (const entity of allEntities) {
          await syncQueueService.addTask(entity, userId, 'full-sync', 2, 20000);
          entitiesSynced.push(entity);
        }
      } else {
        strategy = 'optimized';
        
        // 3. Lazy loading: Sync entità non critiche in background
        if (this.config.enableLazyLoading) {
          this.scheduleLazySync(userId);
        }

        // 4. Prefetch: Prepara dati per uso futuro
        if (this.config.enablePrefetch) {
          this.schedulePrefetch(userId);
        }
      }

      const duration = Date.now() - startTime;
      
      OfflineLogger.info('SyncOptimizationService', 
        `Optimized sync completed in ${duration}ms using ${strategy} strategy. Entities: ${entitiesSynced.join(', ')}`
      );

      return {
        success: true,
        strategy,
        entitiesSynced,
        duration
      };

    } catch (error) {
      OfflineLogger.error('SyncOptimizationService.performOptimizedInitialSync', error);
      
      return {
        success: false,
        strategy: 'failed',
        entitiesSynced,
        duration: Date.now() - startTime
      };
    }
  }

  private scheduleLazySync(userId: string): void {
    OfflineLogger.info('SyncOptimizationService', 'Scheduling lazy sync for non-critical entities');
    
    setTimeout(async () => {
      if (networkService.isOnline()) {
        // Sync entità non critiche con priorità bassa
        const nonCriticalEntities = ['shops', 'recurringProducts'];
        
        for (const entity of nonCriticalEntities) {
          await syncQueueService.addTask(entity, userId, 'full-sync', 4, 25000);
        }
      }
    }, 10000); // 10 secondi di delay
  }

  private schedulePrefetch(userId: string): void {
    if (this.prefetchTimer) {
      clearTimeout(this.prefetchTimer);
    }

    this.prefetchTimer = setTimeout(async () => {
      if (networkService.isOnline() && this.isIdleTime()) {
        OfflineLogger.info('SyncOptimizationService', 'Starting prefetch during idle time');
        
        try {
          // Prefetch dati che potrebbero servire (es. immagini prodotti più usati)
          await this.prefetchProductImages(userId);
          await this.prefetchRecentlyUsedData(userId);
        } catch (error) {
          OfflineLogger.error('SyncOptimizationService.schedulePrefetch', error);
        }
      }
    }, this.config.prefetchDelay);
  }

  private isIdleTime(): boolean {
    // Controlla se è un momento di bassa attività
    const now = new Date();
    const hour = now.getHours();
    
    // Considera "idle" le ore notturne (23-06) o pause pranzo (12-14)
    return (hour >= 23 || hour <= 6) || (hour >= 12 && hour <= 14);
  }

  private async prefetchProductImages(userId: string): Promise<void> {
    try {
      // Qui potresti implementare prefetch di immagini prodotti più usati
      OfflineLogger.info('SyncOptimizationService', 'Prefetching product images');
      
      // Placeholder per logica di prefetch immagini
      // const mostUsedProducts = await getMostUsedProducts(userId);
      // await preloadImages(mostUsedProducts);
      
    } catch (error) {
      OfflineLogger.error('SyncOptimizationService.prefetchProductImages', error);
    }
  }

  private async prefetchRecentlyUsedData(userId: string): Promise<void> {
    try {
      OfflineLogger.info('SyncOptimizationService', 'Prefetching recently used data');
      
      // Prefetch dati usati di recente ma con priorità bassa
      await syncQueueService.addTask('shoppingItems', userId, 'incremental', 5, 30000);
      
    } catch (error) {
      OfflineLogger.error('SyncOptimizationService.prefetchRecentlyUsedData', error);
    }
  }

  // Cache management per ridurre richieste di rete
  setCacheEntry(key: string, data: any, ttl?: number): void {
    const actualTtl = ttl || this.config.defaultTtl;
    const size = this.estimateSize(data);
    
    // Controlla se abbiamo spazio in cache
    if (this.currentCacheSize + size > this.maxCacheSize) {
      this.evictLeastRecentlyUsed(size);
    }

    const entry: CacheEntry = {
      key,
      data: this.config.compressionEnabled ? this.compressData(data) : data,
      timestamp: Date.now(),
      ttl: actualTtl,
      size
    };

    this.cache.set(key, entry);
    this.currentCacheSize += size;

    OfflineLogger.info('SyncOptimizationService', `Cached entry ${key} (${size} bytes, TTL: ${actualTtl}ms)`);
  }

  getCacheEntry(key: string): any | null {
    const entry = this.cache.get(key);

    if (!entry) {
      this.cacheMisses++;
      return null;
    }

    // Controlla TTL
    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      this.currentCacheSize -= entry.size;
      this.cacheMisses++;
      return null;
    }

    this.cacheHits++;
    return this.config.compressionEnabled ? this.decompressData(entry.data) : entry.data;
  }

  private evictLeastRecentlyUsed(neededSpace: number): void {
    const entries = Array.from(this.cache.entries())
      .sort(([, a], [, b]) => a.timestamp - b.timestamp);

    let freedSpace = 0;
    for (const [key, entry] of entries) {
      this.cache.delete(key);
      this.currentCacheSize -= entry.size;
      freedSpace += entry.size;
      
      if (freedSpace >= neededSpace) {
        break;
      }
    }

    OfflineLogger.info('SyncOptimizationService', `Evicted cache entries, freed ${freedSpace} bytes`);
  }

  private estimateSize(data: any): number {
    try {
      return new Blob([JSON.stringify(data)]).size;
    } catch {
      return JSON.stringify(data).length * 2; // Fallback estimation
    }
  }

  private compressData(data: any): string {
    // Placeholder per compressione (LZ-string o simile)
    return JSON.stringify(data);
  }

  private decompressData(compressedData: string): any {
    // Placeholder per decompressione
    return JSON.parse(compressedData);
  }

  updateConfig(newConfig: Partial<OptimizationConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.maxCacheSize = this.config.maxCacheSize;
    
    OfflineLogger.info('SyncOptimizationService', `Configuration updated: ${JSON.stringify(this.config)}`);
  }

  getCacheStats(): { size: number; entries: number; hitRate: number } {
    const total = this.cacheHits + this.cacheMisses;
    const hitRate = total > 0 ? this.cacheHits / total : 0;

    return {
      size: this.currentCacheSize,
      entries: this.cache.size,
      hitRate
    };
  }

  resetCacheStats(): void {
    this.cacheHits = 0;
    this.cacheMisses = 0;
  }

  clearCache(): void {
    this.cache.clear();
    this.currentCacheSize = 0;
    this.resetCacheStats();
    OfflineLogger.info('SyncOptimizationService', 'Cache cleared');
  }
}

export const syncOptimizationService = new SyncOptimizationService();
